import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

const PT_BR_DATE_FORMAT = 'dd/MM/yyyy, HH:mm:ss';
const PT_BR_DATE_FORMAT_YEAR = 'LLL d y, h:mm a';

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (
  time,
  dateFormat = PT_BR_DATE_FORMAT
) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat, { locale: ptBR });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, PT_BR_DATE_FORMAT_YEAR, { locale: ptBR });
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  if (!time) return '';
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: ptBR,
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = PT_BR_DATE_FORMAT) => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df, { locale: ptBR });
};

const shortTimestampPortuguese = (time, withAgo) => {
  if (!time) return '';

  if (time === '' || time.includes('menos de um minuto')) {
    return 'agora';
  }

  if (time.includes('meio minuto')) {
    return '30s';
  }

  const suffix = withAgo ? ' atrás' : '';

  return time
    .replace(/há |cerca de |quase /g, '')
    .replace(/ um minuto/, `1m${suffix}`)
    .replace(/ uma hora/, `1h${suffix}`)
    .replace(/ um dia/, `1d${suffix}`)
    .replace(/ um mês/, `1mês${suffix}`)
    .replace(/ um ano/, `1a${suffix}`)
    .replace(/ minutos?/, `m${suffix}`)
    .replace(/ horas?/, `h${suffix}`)
    .replace(/ dias?/, `d${suffix}`)
    .replace(/ meses/, `meses${suffix}`)
    .replace(/ mês/, `mês${suffix}`)
    .replace(/ anos?/, `a${suffix}`)
    .replace(/\s/g, '');
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append suffix.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) =>
  shortTimestampPortuguese(time, withAgo);

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
